/* Line & Grain — 3D product viewer (sections/lg-3d-viewer.liquid).
 *
 * Config (product metafield custom.web_3d_config, JSON):
 * {
 *   "version": 1,
 *   "fabric_material": "MAT_Fabric_Linen",
 *   "styles": [ { "variant_id": 123, "glb": "https://…/x.glb", "usdz": "https://…/x.usdz" }, … ],
 *     (chip text = variant title; usdz is used as ios-src only when "ios_ar": "usdz")
 *   "ios_ar": "generate" | "usdz"   (default "generate": USDZ built on the fly, reflects colour)
 *   "material": { "sheen": 0.06, "specular": 0.25 }   (optional overrides applied after each load)
 *   "look": { "tone_mapping": "commerce", "exposure": 0.8 }   (optional viewer overrides)
 *   "colors": [ { "name": "White 70805", "texture": "https://…/fabric.jpg", "swatch": "#f2efe9" }, … ]
 * }
 * glb / texture / swatch values without a scheme or leading slash resolve against the theme's
 * assets folder (data-asset-base), e.g. "lg3d-cloud-sofa-4pc_L_ottoman.glb".
 * Page ↔ 3D sync: listens to the theme's `on:variant:change`; choosing a style in 3D selects the
 * matching option inputs in the theme's <variant-picker>, so price / cart / URL stay theme-driven.
 */
(() => {
  // 3.4.0 (not 4.x): lets iOS Safari AND Chrome generate AR Quick Look USDZ on the fly from the
  // current scene, so AR shows the selected colour (4.x requires a fixed ios-src on iOS Chrome).
  const MV_SRC = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.4.0/dist/model-viewer.min.js';
  // Look approved by Jerry 2026-09-25 in the prototype tuning panel. 'commerce' tone mapping keeps
  // texture colours true (3.4.0's default 'auto' = ACES, which washed colours out); the exported GLBs
  // also carry full-white sheen / specular 0.6, overridden here after each load.
  const LOOK_DEFAULTS = { tone_mapping: 'commerce', exposure: 0.8 };
  const MATERIAL_DEFAULTS = { sheen: 0.06, specular: 0.25 };
  const IN_APP = /(MicroMessenger|Instagram|FBAN|FBAV|Line\/|TikTok|musical_ly|Pinterest|Snapchat)/i;

  function ensureModelViewer() {
    if (customElements.get('model-viewer') || document.querySelector('script[data-lg3d-mv]')) return;
    const s = document.createElement('script');
    s.type = 'module';
    s.src = MV_SRC;
    s.dataset.lg3dMv = '';
    document.head.appendChild(s);
  }

  class LG3DViewer extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-lg3d-config]').textContent);
      } catch (e) {
        console.warn('[lg-3d-viewer] invalid config', e);
        return;
      }
      const base = this.dataset.assetBase || '';
      const resolve = (u) => (!u || /^(https?:)?\/\//.test(u) || u.startsWith('/') ? u : base + u);
      try {
        this.variants = JSON.parse(this.querySelector('[data-lg3d-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }
      const variantById = (id) => this.variants.find((v) => v.id === Number(id));
      // Chip text = the product's own option names, so 3D and the page always match.
      this.styles = (this.config.styles || []).filter((s) => s.glb).map((s) => {
        const v = variantById(s.variant_id);
        return { ...s, glb: resolve(s.glb), usdz: resolve(s.usdz), label: v ? v.title : (s.label || String(s.variant_id)) };
      });
      // Compact toolbar: drop the words every option shares (e.g. "Ultra-Soft Cloud"), keep the rest
      // exactly as the page option names; the full title stays in title/aria-label.
      if (this.styles.length > 1) {
        const words = this.styles.map((s) => s.label.split(' '));
        let n = 0;
        while (words.every((w) => w.length > n + 1 && w[n] === words[0][n])) n += 1;
        this.styles.forEach((s, i) => { s.short = words[i].slice(n).join(' '); });
      }
      this.colors = (this.config.colors || []).filter((c) => c.texture)
        .map((c) => ({ ...c, texture: resolve(c.texture), swatch: c.swatch && !c.swatch.startsWith('#') ? resolve(c.swatch) : c.swatch }));
      if (!this.styles.length) return;

      this.mv = this.querySelector('model-viewer');
      const look = { ...LOOK_DEFAULTS, ...(this.config.look || {}) };
      this.mv.toneMapping = look.tone_mapping;
      this.mv.exposure = look.exposure;
      this.bar = this.querySelector('.lg3d__bar i');
      this.texCache = new Map();
      this.color = this.colors[0] || null;
      ensureModelViewer();
      this.renderStyles();
      this.renderColors();
      this.bindToolbar();
      this.bindViewer();

      document.addEventListener('on:variant:change', (e) => {
        if (this.keepScrollY != null) {
          // Theme re-renders the product section; keep the shopper where they are.
          const y = this.keepScrollY;
          this.keepScrollY = null;
          if (Math.abs(window.scrollY - y) > 2) window.scrollTo({ top: y, behavior: 'instant' });
        }
        const id = e.detail && e.detail.variant && e.detail.variant.id;
        if (id) this.showVariant(id, false);
      });
      this.showVariant(this.currentVariantId(), false);
    }

    currentVariantId() {
      const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      return input ? Number(input.value) : null;
    }

    styleFor(variantId) {
      return this.styles.find((s) => Number(s.variant_id) === Number(variantId)) || null;
    }

    showVariant(variantId, syncPage) {
      const style = this.styleFor(variantId) || (this.current ? null : this.styles[0]);
      if (!style || style === this.current) return;
      this.current = style;
      this.querySelectorAll('[data-lg3d-style]').forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.lg3dStyle === String(style.variant_id)));
      });
      this.bar.style.width = '0';
      // iOS AR: by default no ios-src, so model-viewer generates the USDZ from what is on screen.
      if (this.config.ios_ar === 'usdz' && style.usdz) this.mv.setAttribute('ios-src', style.usdz);
      else this.mv.removeAttribute('ios-src');
      this.mv.src = style.glb;
      if (syncPage) this.selectOnPage(style.variant_id);
    }

    selectOnPage(variantId) {
      if (Number(variantId) === this.currentVariantId()) return;
      const variant = this.variants.find((v) => v.id === Number(variantId));
      const picker = document.querySelector('variant-picker');
      if (!variant || !picker) return;
      // noFocus: the theme otherwise focuses the option input after re-rendering,
      // which scrolls the page back up to the product form.
      const fire = (el) => el.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { noFocus: true } }));
      this.keepScrollY = window.scrollY;
      variant.options.forEach((value, i) => {
        const set = picker.querySelector(`.option-selector[data-index="${i + 1}"]`);
        if (!set) return;
        const radio = [...set.querySelectorAll('input[type="radio"]')].find((r) => r.value === value);
        if (radio) {
          if (!radio.checked) { radio.checked = true; fire(radio); }
          return;
        }
        const select = set.querySelector('select');
        if (select && select.value !== value) { select.value = value; fire(select); }
      });
    }

    renderStyles() {
      const box = this.querySelector('[data-lg3d-styles]');
      if (this.styles.length < 2) return;
      this.styles.forEach((s) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lg3d__pill lg3d__chip';
        b.textContent = s.short || s.label;
        b.title = s.label;
        b.setAttribute('aria-label', s.label);
        b.dataset.lg3dStyle = String(s.variant_id);
        b.setAttribute('aria-pressed', 'false');
        b.addEventListener('click', () => this.showVariant(s.variant_id, true));
        box.appendChild(b);
      });
      this.querySelector('[data-lg3d-open="styles"]').hidden = false;
    }

    renderColors() {
      const box = this.querySelector('[data-lg3d-colors]');
      const note = this.querySelector('[data-lg3d-note]');
      if (!this.colors.length) return;
      if (note) note.hidden = false;
      if (this.colors.length < 2) return;   // a single colour needs no picker
      this.colors.forEach((c) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lg3d__swatch';
        b.title = c.name;
        b.setAttribute('aria-label', c.name);
        const dot = document.createElement('i');
        dot.style.background = c.swatch && c.swatch.startsWith('#') ? c.swatch : `url("${c.swatch || c.texture}") center / 220%`;
        const name = document.createElement('span');
        name.textContent = c.name;              // shown only on the selected swatch
        b.append(dot, name);
        b.setAttribute('aria-pressed', String(c === this.color));
        b.addEventListener('click', () => {
          this.color = c;
          box.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          this.applyColor();
        });
        box.appendChild(b);
      });
      this.querySelector('[data-lg3d-open="colors"]').hidden = false;
    }

    showPanel(name) {
      this.querySelectorAll('[data-lg3d-panel]').forEach((p) => { p.hidden = p.dataset.lg3dPanel !== name; });
      const scroller = this.querySelector(`[data-lg3d-panel="${name}"] .lg3d__scroll`);
      const active = scroller && scroller.querySelector('[aria-pressed="true"]');
      if (active) {
        // centre the selected option; rect maths (offsetLeft is relative to the stage, not the scroller)
        const delta = active.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
        scroller.scrollTo({ left: scroller.scrollLeft + delta - (scroller.clientWidth - active.clientWidth) / 2, behavior: 'instant' });
      }
      const focusEl = this.querySelector(`[data-lg3d-panel="${name}"] button`);
      if (focusEl) focusEl.focus({ preventScroll: true });
    }

    bindToolbar() {
      const orbit = this.mv.getAttribute('camera-orbit');
      this.querySelectorAll('[data-lg3d-open]').forEach((b) => b.addEventListener('click', () => this.showPanel(b.dataset.lg3dOpen)));
      this.querySelectorAll('[data-lg3d-close]').forEach((b) => b.addEventListener('click', () => this.showPanel('main')));
      this.querySelector('[data-lg3d-reset]').addEventListener('click', () => {
        this.mv.cameraOrbit = orbit;
        this.mv.cameraTarget = 'auto auto auto';
        this.mv.fieldOfView = 'auto';
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.showPanel('main'); });
    }

    fabricMaterial() {
      return this.mv.model && this.mv.model.materials.find((m) => m.name === (this.config.fabric_material || 'MAT_Fabric_Linen'));
    }

    tuneMaterial() {
      const mat = this.fabricMaterial();
      if (!mat) return;
      const cfg = { ...MATERIAL_DEFAULTS, ...(this.config.material || {}) };
      try {
        if (typeof cfg.sheen === 'number') mat.setSheenColorFactor([cfg.sheen, cfg.sheen, cfg.sheen]);
        if (typeof cfg.specular === 'number') mat.setSpecularFactor(cfg.specular);
      } catch (e) { /* material without these extensions: leave as exported */ }
    }

    async applyColor() {
      if (!this.color || !this.mv.model) return;
      const mat = this.fabricMaterial();
      const slot = mat && mat.pbrMetallicRoughness.baseColorTexture;
      if (!slot) return;
      if (!this.texCache.has(this.color.texture)) {
        this.texCache.set(this.color.texture, await this.mv.createTexture(this.color.texture));
      }
      slot.setTexture(this.texCache.get(this.color.texture));
    }

    bindViewer() {
      this.mv.addEventListener('progress', (e) => {
        const p = e.detail.totalProgress;
        this.bar.style.width = p >= 1 ? '0' : `${p * 100}%`;
      });
      this.mv.addEventListener('load', async () => {
        this.tuneMaterial();
        if (this.colors.length > 1 || (this.color && this.colors.indexOf(this.color) > 0)) await this.applyColor();
        this.bar.style.width = '0';
        const hint = this.querySelector('.lg3d__ar-hint');
        const mobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
        hint.hidden = !(mobile && !this.mv.canActivateAR && IN_APP.test(navigator.userAgent));
      });
    }
  }

  if (!customElements.get('lg-3d-viewer')) customElements.define('lg-3d-viewer', LG3DViewer);
})();
